import { computed, Signal } from '@angular/core';
import { AsyncValidatorFn, ValidatorFn, Validators } from '@angular/forms';
import { coerceObservable, isNotEmpty, numericFormat, TFileType } from '@partners/utils';
import { Observable, of } from 'rxjs';
import { ValidationPatterns } from '../validators';
import {
  CustomDateValidators,
  CustomListValidators,
  CustomRangeValidators,
  CustomServerValidators,
  CustomValidators
} from '../validators/validators-registry';
import {
  IDateRangeValidationHandler,
  IFieldValidation,
  IFileTypeValidationHandler,
  IFileValidationHandler,
  IListValidationHandler,
  INumericValidationHandler,
  IRangeValidationHandler,
  IServerValidationHandler,
  IValidationHandler
} from './form.interface';
import { TIterableOption, TValidator, TValidatorError } from './form.types';

export class FieldValidation implements IFieldValidation {
  label?: string;
  validationHandlers?: IValidationHandler[];

  constructor({ validationHandlers: validators = [], label = undefined }: IFieldValidation) {
    this.label = label;
    this.validationHandlers = validators;

    /**
     * the validation label is common to all field's validators, it's used to format an informative
     * error messages with the field's name or caption form extra specificity. here, we copy to root
     * label of the field's validation to all validators
     */
    validators.map((v) => {
      v.label = label;
    });
  }
}

export abstract class ValidationHandler implements IValidationHandler {
  value?: unknown = null;
  label?: string;
  type?: TValidator | TValidator[];
  pattern?: RegExp;
  message?: string | Observable<string>;
  isAsync?: boolean;
  priority?: number;
  validators?: ValidatorFn[];
  asyncValidators?: AsyncValidatorFn[];

  get hasRequiredValidator(): boolean {
    const requiredValidator = this.validators.find((v) => v === Validators.required);
    return isNotEmpty(requiredValidator);
  }

  constructor({ label, message, validators, type, pattern, asyncValidators }: IValidationHandler) {
    this.label = label;
    this.type = type;
    this.pattern = pattern;
    this.message = message;
    this.validators = validators;
    this.asyncValidators = asyncValidators;

    if (!Array.isArray(this.validators)) {
      this.validators = new Array<ValidatorFn>();
    }
    if (!Array.isArray(this.asyncValidators)) {
      this.validators = new Array<AsyncValidatorFn>();
    }
  }

  /**
   * create an validation error message on demand (see field-subscript directive)
   * @param message
   * @returns a stream of the error message string value
   */
  abstract buildMessage(validatorType: TValidator, error?: TValidatorError): Observable<string>;

  protected formatMessage(message?: string | Observable<string>) {
    if (!message) {
      return of('אירעה שגיאה');
    }
    return coerceObservable(message);
  }
}
export abstract class RangeValidationHandler<T> extends ValidationHandler implements IRangeValidationHandler<T> {
  max: T;
  min: T;
  specific: T[];

  get isMinMaxEqual(): boolean {
    return this.max === this.min;
  }

  constructor(args: IRangeValidationHandler<T>) {
    super(args);
    const { min, max, specific } = args;
    this.max = max;
    this.min = min;
    this.specific = specific;
  }
}
export abstract class ListValidationHandler extends ValidationHandler implements IListValidationHandler {
  options$$: Signal<TIterableOption[]>;
  query$$: Signal<string>;
  keyProp: string;

  constructor(args?: IListValidationHandler) {
    super(args);
    const { options$$, query$$ } = args;
    this.options$$ = computed(() => options$$());
    this.query$$ = computed(() => query$$());
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `יש לבחור ערך מהרשימה`);
  }
}
export class RequiredValidation extends ValidationHandler {
  constructor(args?: IValidationHandler) {
    args = { ...args, ...{ type: 'required' } };

    super({ ...args });

    this.validators?.push(Validators.required);
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `נראה שחסר כאן ${this.label || 'ערך'}`);
  }
}

export class RequiredTrueValidation extends ValidationHandler {
  constructor(args?: IValidationHandler) {
    args = { ...args, ...{ type: 'required' } };

    super({ ...args });

    this.validators?.push(Validators.requiredTrue);
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `נראה שחסר כאן ${this.label || 'ערך'}`);
  }
}
export class PatternValidation extends ValidationHandler {
  constructor(args?: IValidationHandler) {
    args = { ...args, ...{ type: 'pattern' } };
    super({ ...args });
    this.validators?.push(CustomValidators.pattern(args));
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `נראה שיש טעות ב${this.label}`);
  }
}
export class NumericValidation extends ValidationHandler {
  constructor(args?: INumericValidationHandler) {
    args = { ...args, ...{ type: 'numeric', pattern: ValidationPatterns.numeric } };

    super({ ...args });
    this.validators?.push(CustomValidators.numeric(args));
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `יש להקליד ספרות בלבד`);
  }
}
export class MaxFileSizeValidation extends ValidationHandler {
  constructor(private args?: Partial<IFileValidationHandler>) {
    args = { ...args, ...{ type: 'maxFileSize' } };

    super({ ...args });
    this.validators?.push(CustomValidators.maxFileSize(args));
  }

  override buildMessage(validatorType: TValidator, error?: TValidatorError): Observable<string> {
    return this.formatMessage(
      this.message || `גודל הקובץ שהעלית גדול מידי (${error.maxFileSize.actual} מ״ב), נצטרך תמונה קטנה יותר`
    );
  }
}
export class MinFileSizeValidation extends ValidationHandler {
  constructor(private args?: Partial<IFileValidationHandler>) {
    args = { ...args, ...{ type: 'minFileSize' } };

    super({ ...args });
    this.validators?.push(CustomValidators.minFileSize(args));
  }

  override buildMessage(validatorType: TValidator, error?: TValidatorError): Observable<string> {
    return this.formatMessage(
      this.message || `גודל הקובץ שהעלית קטן מידי (${error.minFileSize.actual} ק״ב), נצטרך תמונה באיכות טובה יותר`
    );
  }
}
export class FileTypeValidation extends ValidationHandler {
  constructor(private args: Partial<IFileTypeValidationHandler>) {
    args = { ...args, ...{ type: 'fileType' } };

    super(args);
    this.validators?.push(CustomValidators.fileType(args));
  }

  override buildMessage(validatorType: TValidator, error?: TValidatorError): Observable<string> {
    const validFileTypes: TFileType[] = error.fileType.allowed;
    const validFileTypesString = validFileTypes.reduce(
      (str, vft, index) => `${str}${index ? ', ' : ''}${vft.split('/')[1]}`,
      ''
    );

    return this.formatMessage(
      this.message || `לא נוכל להשתמש בתמונה. אפשר להעלות רק קבצים מסוג ${validFileTypesString}`
    );
  }
}
export class MinLengthValidation extends ValidationHandler {
  constructor(private args: Partial<IRangeValidationHandler<number>>) {
    super({ ...args, ...{ type: 'minLength' } });
    this.validators?.push(CustomRangeValidators.minLength(args));
  }
  override buildMessage(validatorType: TValidator, error?: TValidatorError): Observable<string> {
    return this.formatMessage(this.message || `יש להזין לפחות ${this.args.min} תווים.`);
  }
}

export class MaxLengthValidation extends ValidationHandler {
  constructor(private args: Partial<IRangeValidationHandler<number>>) {
    super({ ...args, ...{ type: 'maxLength' } });
    this.validators?.push(CustomRangeValidators.maxLength(args));
  }
  override buildMessage(validatorType: TValidator, error?: TValidatorError): Observable<string> {
    return this.formatMessage(this.message || `יש להזין עד ${this.args.max} תווים`);
  }
}

export class MultiplierValidation extends ValidationHandler {
  constructor(args?: INumericValidationHandler) {
    args = { ...args, ...{ type: 'multiplier' } };

    super({ ...args });

    // this.validators?.push(CustomValidators.numeric(args));
    this.validators?.push(CustomValidators.multiplier(args));
  }

  override buildMessage(validatorType: TValidator, error: any): Observable<string> {
    return this.formatMessage(this.message || `יש להזין סכום בכפולות של ${error['multiplier']}`);
  }
}

export class MinNumberValidation extends RangeValidationHandler<number> {
  get minNumberF(): string {
    return numericFormat(this.min);
  }

  constructor(private args: IRangeValidationHandler<number>) {
    super({ ...args, ...{ type: 'minNumber' } });
    this.validators?.push(CustomRangeValidators.minNumber(args));
  }
  override buildMessage(validatorType: TValidator, error?: TValidatorError): Observable<string> {
    return this.formatMessage(this.message || `יש להזין ערך גבוה מ-${this.minNumberF}`);
  }
}

export class MaxNumberValidation extends RangeValidationHandler<number> {
  get maxNumberF(): string {
    return numericFormat(this.max);
  }
  constructor(private args: IRangeValidationHandler<number>) {
    super({ ...args, ...{ type: 'maxNumber' } });
    this.validators?.push(CustomRangeValidators.maxNumber(args));
  }
  override buildMessage(validatorType: TValidator, error?: TValidatorError): Observable<string> {
    return this.formatMessage(this.message || `יש להזין ערך נמוך מ-${this.maxNumberF}`);
  }
}
export class NumericRangeValidation extends RangeValidationHandler<number> {
  get minValueF(): string {
    return numericFormat(this.min);
  }

  get maxValueF(): string {
    return numericFormat(this.max);
  }

  constructor(args?: IRangeValidationHandler<number>) {
    args = { ...args, ...{ type: ['minNumber', 'maxNumber'] } };

    super({ ...args });

    this.validators.push(CustomRangeValidators.minNumber(args));
    this.validators.push(CustomRangeValidators.maxNumber(args));
  }

  override buildMessage(validatorType: TValidator): Observable<string> {
    if (this.isMinMaxEqual) {
      return this.formatMessage(this.message || `יש להזין ערך מדויק ${this.min}`);
    }
    if (validatorType === 'minNumber') {
      return this.formatMessage(this.message || `יש להזין ערך גבוה מ  ${this.minValueF}`);
    }
    if (validatorType === 'maxNumber') {
      return this.formatMessage(this.message || `יש להזין ערך נמוך מ  ${this.maxValueF}`);
    }

    return this.formatMessage(this.message || `יש להזין ערך בין ${this.minValueF} ל ${this.maxValueF}`);
  }
}
export class DateRangeValidation extends RangeValidationHandler<string> {
  get minDateF(): string {
    return this.min;
  }

  get maxDateF(): string {
    return this.max;
  }

  constructor(args?: IDateRangeValidationHandler<string>) {
    super({ ...args, ...{ type: 'dateRange' } });

    // this.validators = this.validators.concat([...args.validators]);
    this.validators.push(CustomDateValidators.dateRange(args));
  }

  override buildMessage(validatorType: TValidator): Observable<string> {
    return this.formatMessage(this.message || `יש להזין ערך בין ${this.minDateF} ל ${this.maxDateF}`);
  }
}
export class LengthValidation extends RangeValidationHandler<number> {
  get minLength(): number {
    return this.min;
  }

  get maxLength(): number {
    return this.max;
  }

  constructor(args?: IRangeValidationHandler<number>) {
    args = { ...args, ...{ type: ['minLength', 'maxLength'] } };

    super({ ...args });

    this.validators?.push(CustomRangeValidators.minLength(args));
    this.validators?.push(CustomRangeValidators.maxLength(args));
  }

  override buildMessage(validatorType: TValidator): Observable<string> {
    if (this.isMinMaxEqual) {
      return this.formatMessage(this.message || `יש להזין בדיוק ${this.minLength} תווים`);
    }
    if (validatorType === 'minLength') {
      return this.formatMessage(this.message || `יש להזין לפחות ${this.minLength} תווים`);
    }
    if (validatorType === 'maxLength') {
      return this.formatMessage(this.message || `יש להזין עד ${this.maxLength} תווים`);
    }

    return this.formatMessage(this.message || `יש להזין בין ${this.minLength} ל ${this.maxLength} תווים`);
  }
}

export class SpecificLengthValidation extends RangeValidationHandler<number> {
  get lengthValues(): number[] {
    return this.specific;
  }

  constructor(args?: IRangeValidationHandler<number>) {
    args = { ...args, ...{ type: ['specificLength'] } };

    super({ ...args });

    this.validators?.push(CustomRangeValidators.specificLength(args));
  }

  override buildMessage(validatorType: TValidator): Observable<string> {
    const options = this.lengthValues.map((v) => ` ${v} `).join('או');

    return this.formatMessage(this.message || `יש להזין ערך באורך ${options} תווים`);
  }
}

export class HebrewCharsValidation extends ValidationHandler {
  constructor(args?: IValidationHandler) {
    args = { ...args, ...{ type: 'hebrewChars', pattern: ValidationPatterns.hebrewChars } };

    super({ ...args });

    this.validators.push(CustomValidators.pattern(args));
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `נראה שיש טעות ב${this.label}`);
  }
}
export class HebrewNameValidation extends ValidationHandler {
  constructor(args?: IValidationHandler) {
    args = { ...args, ...{ type: 'hebrewName', pattern: ValidationPatterns.hebrewName } };

    super({ ...args });

    this.validators.push(CustomValidators.hebrewName(args));
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `נראה שיש טעות ב${this.label}, כדאי לבדוק מול תעודת הזהות`);
  }
}
export class EnglishNameValidation extends ValidationHandler {
  constructor(args?: IValidationHandler) {
    args = { ...args, ...{ type: 'englishName', pattern: ValidationPatterns.englishName } };

    super({ ...args });

    this.validators.push(CustomValidators.englishName(args));
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `נראה שיש טעות ב${this.label}`);
  }
}
export class EmailValidation extends ValidationHandler {
  constructor(args?: IValidationHandler) {
    args = { ...args, ...{ type: 'email', pattern: ValidationPatterns.email } };

    super({ ...args });

    this.validators.push(CustomValidators.email(args));
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `נראה שיש טעות ב${this.label}`);
  }
}
export class IdNumberValidation extends ValidationHandler {
  constructor(args?: IValidationHandler) {
    args = { ...args, ...{ type: 'idNumber', pattern: ValidationPatterns.idNumber } };

    super({ ...args });

    this.validators.push(CustomValidators.idNumber(args));
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `נראה שיש טעות ב${this.label}`);
  }
}
export class SeverityValidation extends ValidationHandler {
  constructor(args?: IServerValidationHandler) {
    args = { ...args, ...{ type: 'severity' } };

    super({ ...args });

    this.validators.push(CustomServerValidators.severity(args));
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `נראה שיש טעות ב${this.label}`);
  }
}
export class MobilePhoneValidation extends ValidationHandler {
  constructor(args?: IValidationHandler) {
    args = { ...args, ...{ type: 'mobilePhone', pattern: ValidationPatterns.mobilePhone } };

    super({ ...args });

    this.validators.push(CustomValidators.mobilePhone(args));
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `נראה שיש טעות ב${this.label}`);
  }
}
export class MaskValidation extends ValidationHandler {
  constructor(args?: IValidationHandler) {
    args = { ...args, ...{ type: 'inputMask' } };

    super({ ...args });
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `נראה שיש טעות ב${this.label}`);
  }
}
export class CreditCardValidation extends ValidationHandler {
  constructor(args?: IValidationHandler) {
    args = { ...args, ...{ type: 'creditCard' } };

    super({ ...args });

    this.validators?.push(CustomValidators.creditCard(args));
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `${this.label || 'לא תקין'}`);
  }
}
export class AutoCompleteValidation extends ListValidationHandler {
  constructor(args?: IListValidationHandler) {
    args = { ...args, ...{ type: 'emptyList' } };

    super({ ...args });

    this.validators?.push(CustomListValidators.emptyList(args));
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `יש לבחור ערך מהרשימה`);
  }
}

export class HebrewConsecutiveCharsValidation extends ValidationHandler {
  constructor(args?: IValidationHandler) {
    args = { ...args, ...{ type: 'hebrewConsecutiveChars', pattern: ValidationPatterns.hebrewPhrase } };

    super({ ...args });

    this.validators.push(CustomValidators.hebrewConsecutiveChars(args));
  }

  override buildMessage(): Observable<string> {
    return this.formatMessage(this.message || `נראה שיש טעות ב${this.label} שהזנת, כדאי לבדוק שוב`);
  }
}
